import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import * as Tone from 'tone';
import { computePeaks, rangeMinMax, sizeCanvas, fillEnvelopeShape } from '../utils/waveform';
import { createLoopBuffer } from '../utils/loopBuffer';
import './SlideDeck.css';

// ---- Talk configuration ---------------------------------------------------
// The amen break inside amen-break-region.wav (which starts at 1:20 of the
// full song). Tweak these two numbers to nail the loop points.
const BREAK_START = 6.2;
const BREAK_END = 13.1;
const REGION_URL = `${process.env.PUBLIC_URL || ''}/talk/amen-break-region.wav`;

const IMAGE_SLIDES = ['slide-01', 'slide-02', 'slide-03', 'slide-04', 'slide-05', 'slide-06']
  .map((n) => `${process.env.PUBLIC_URL || ''}/talk/${n}.png`);

const ACCENT = '#FF4141';
const CHOP_RED = '#E03131';
const CHOP_RED_RGB = '224, 49, 49';
const IDLE_WAVE = '#DCDCDC';
const WAVE_HEIGHT = 220;
const CELL_HEIGHT = 110;

// Envelope-clipped black seeker (same look as the app)
function drawSeeker(ctx, peaks, tFrac0, tFrac1, x, cssH) {
  const [mn, mx] = rangeMinMax(peaks, tFrac0, tFrac1);
  const mid = cssH / 2;
  const amp = (cssH - 6) / 2;
  let yT = mid - mx * amp;
  let yB = mid - mn * amp;
  if (yB - yT < 6) { yT = mid - 3; yB = mid + 3; }
  ctx.fillStyle = '#111111';
  ctx.fillRect(x - 1, yT, 2, yB - yT);
}

// Shared decode cache for the region file
let regionBufferPromise = null;
function loadRegionBuffer() {
  if (!regionBufferPromise) {
    regionBufferPromise = fetch(REGION_URL)
      .then((r) => r.arrayBuffer())
      .then((ab) => Tone.context.decodeAudioData(ab));
  }
  return regionBufferPromise;
}

/**
 * Slide: the region plays, and as the playhead crosses the break, the break
 * "gets grabbed" — masked in red on the main waveform while a chop cell
 * fills in below, exactly like chopping in sample.band.
 */
const GrabSlide = ({ active }) => {
  const mainRef = useRef(null);
  const cellRef = useRef(null);
  const playerRef = useRef(null);
  const startedAtRef = useRef(null); // Tone.now() when playback began
  const [buffer, setBuffer] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    let dead = false;
    loadRegionBuffer().then((b) => { if (!dead) setBuffer(b); });
    return () => { dead = true; };
  }, []);

  const peaks = useMemo(() => (buffer ? computePeaks(buffer) : null), [buffer]);
  const duration = buffer?.duration || 0;

  const stop = useCallback(() => {
    if (playerRef.current) {
      try { playerRef.current.stop(); } catch { /* not started */ }
      playerRef.current.dispose();
      playerRef.current = null;
    }
    setIsPlaying(false);
  }, []);

  const toggle = useCallback(async () => {
    if (isPlaying) { stop(); return; }
    if (!buffer) return;
    await Tone.start();
    const player = new Tone.Player(buffer).toDestination();
    player.start();
    playerRef.current = player;
    startedAtRef.current = Tone.now();
    setIsPlaying(true);
  }, [isPlaying, buffer, stop]);

  useEffect(() => { if (!active) stop(); }, [active, stop]);
  useEffect(() => stop, [stop]);

  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.code === 'Space') { e.preventDefault(); toggle(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, toggle]);

  // Draw loop: gray region; as the playhead passes through the break it
  // masks red (growing) and the cell below fills in.
  useEffect(() => {
    let raf;
    const tick = () => {
      const canvas = mainRef.current;
      if (canvas && peaks && duration) {
        const playing = !!playerRef.current;
        const t = playing ? Math.min(duration, Tone.now() - startedAtRef.current) : 0;
        if (playing && t >= duration) stop();

        // How much of the break has been "grabbed" so far
        const grabEnd = Math.min(Math.max(t, BREAK_START), BREAK_END);
        const grabbed = grabEnd > BREAK_START;

        const cssW = canvas.clientWidth;
        const ctx = sizeCanvas(canvas, cssW, WAVE_HEIGHT);
        ctx.clearRect(0, 0, cssW, WAVE_HEIGHT);
        const px = cssW / duration;

        const segs = grabbed
          ? [[0, BREAK_START, IDLE_WAVE], [BREAK_START, grabEnd, CHOP_RED], [grabEnd, duration, IDLE_WAVE]]
          : [[0, duration, IDLE_WAVE]];
        for (const [a, b, color] of segs) {
          if (b <= a) continue;
          fillEnvelopeShape(ctx, peaks, duration, a, b, a * px, b * px, WAVE_HEIGHT, color);
        }
        if (playing) {
          ctx.fillStyle = ACCENT;
          ctx.fillRect(Math.min(cssW - 2, t * px), 0, 2, WAVE_HEIGHT);
        }

        // The chop cell below fills in as the break is grabbed
        const cell = cellRef.current;
        if (cell) {
          if (grabbed) {
            const fullW = Math.round(cell.parentElement.clientWidth);
            const frac = (grabEnd - BREAK_START) / (BREAK_END - BREAK_START);
            const w = Math.max(2, Math.round(fullW * frac));
            cell.style.width = `${w}px`;
            cell.style.height = `${CELL_HEIGHT}px`;
            const cctx = sizeCanvas(cell, w, CELL_HEIGHT);
            cctx.clearRect(0, 0, w, CELL_HEIGHT);
            fillEnvelopeShape(cctx, peaks, duration, BREAK_START, grabEnd, 0, w, CELL_HEIGHT, CHOP_RED);
          } else {
            const cctx = cell.getContext('2d');
            cctx && cctx.clearRect(0, 0, cell.width, cell.height);
            cell.style.width = '0px';
          }
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [peaks, duration, stop]);

  return (
    <div className="deck-audio-slide">
      <h2 className="deck-audio-title">"Amen, Brother" · The Winstons, 1969</h2>
      <div className="deck-audio-sub">the break hits at the drum solo · watch it get grabbed</div>
      {buffer ? (
        <>
          <canvas ref={mainRef} className="deck-wave" style={{ height: WAVE_HEIGHT }} onClick={toggle} />
          <div className="deck-cell-row">
            <canvas ref={cellRef} className="deck-cell" />
          </div>
          <div className="deck-audio-hint">{isPlaying ? 'space to stop' : 'press space to play'}</div>
        </>
      ) : (
        <div className="deck-loading">loading…</div>
      )}
    </div>
  );
};

/**
 * Slide: the grabbed break, big, looping — full red while it loops with the
 * seeker cycling through it (the merry-go-round).
 */
const LoopSlide = ({ active }) => {
  const canvasRef = useRef(null);
  const playerRef = useRef(null);
  const startedAtRef = useRef(0);
  const [buffer, setBuffer] = useState(null);
  const [isLooping, setIsLooping] = useState(false);

  useEffect(() => {
    let dead = false;
    loadRegionBuffer().then((b) => { if (!dead) setBuffer(b); });
    return () => { dead = true; };
  }, []);

  const peaks = useMemo(() => (buffer ? computePeaks(buffer) : null), [buffer]);
  const duration = buffer?.duration || 0;

  const stop = useCallback(() => {
    if (playerRef.current) {
      try { playerRef.current.stop(); } catch { /* not started */ }
      playerRef.current.dispose();
      playerRef.current = null;
    }
    setIsLooping(false);
  }, []);

  const toggle = useCallback(async () => {
    if (isLooping) { stop(); return; }
    if (!buffer) return;
    await Tone.start();
    const loopBuffer = createLoopBuffer(buffer, BREAK_START, BREAK_END);
    const player = new Tone.Player(loopBuffer).toDestination();
    player.loop = true;
    player.start();
    playerRef.current = player;
    startedAtRef.current = performance.now();
    setIsLooping(true);
  }, [isLooping, buffer, stop]);

  useEffect(() => { if (!active) stop(); }, [active, stop]);
  useEffect(() => stop, [stop]);

  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.code === 'Space') { e.preventDefault(); toggle(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, toggle]);

  useEffect(() => {
    let raf;
    const loopLen = BREAK_END - BREAK_START;
    const tick = () => {
      const canvas = canvasRef.current;
      if (canvas && peaks && duration) {
        const cssW = canvas.clientWidth;
        const ctx = sizeCanvas(canvas, cssW, WAVE_HEIGHT);
        ctx.clearRect(0, 0, cssW, WAVE_HEIGHT);
        const color = isLooping ? CHOP_RED : `rgba(${CHOP_RED_RGB}, 0.35)`;
        fillEnvelopeShape(ctx, peaks, duration, BREAK_START, BREAK_END, 0, cssW, WAVE_HEIGHT, color);
        if (isLooping) {
          const pos = BREAK_START + (((performance.now() - startedAtRef.current) / 1000) % loopLen);
          const x = ((pos - BREAK_START) / loopLen) * cssW;
          drawSeeker(ctx, peaks, pos / duration, (pos + loopLen / cssW) / duration, Math.min(cssW - 2, x), WAVE_HEIGHT);
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [peaks, duration, isLooping]);

  return (
    <div className="deck-audio-slide">
      <h2 className="deck-audio-title">the merry-go-round</h2>
      <div className="deck-audio-sub">six seconds, looped forever · the most sampled drums in history</div>
      {buffer ? (
        <>
          <canvas ref={canvasRef} className="deck-wave" style={{ height: WAVE_HEIGHT }} onClick={toggle} />
          <div className="deck-audio-hint">{isLooping ? 'space to stop' : 'press space to loop it'}</div>
        </>
      ) : (
        <div className="deck-loading">loading…</div>
      )}
    </div>
  );
};

/**
 * SlideDeck — the talk. Arrow keys move forward/back through the Figma intro
 * slides and the interactive break slides; advancing past the last slide
 * exits into sample.band itself (the demo).
 */
const SlideDeck = ({ onExit }) => {
  const [index, setIndex] = useState(0);

  const slides = useMemo(() => [
    ...IMAGE_SLIDES.map((src) => ({ type: 'image', src })),
    { type: 'grab' },
    { type: 'loop' }
  ], []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setIndex((i) => {
          if (i >= slides.length - 1) { onExit(); return i; }
          return i + 1;
        });
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setIndex((i) => Math.max(0, i - 1));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [slides.length, onExit]);

  // Preload neighbor images
  useEffect(() => {
    for (const s of [slides[index + 1], slides[index + 2]]) {
      if (s?.type === 'image') { const img = new Image(); img.src = s.src; }
    }
  }, [index, slides]);

  const slide = slides[index];

  return (
    <div className="deck">
      {slide.type === 'image' && (
        <img className="deck-image" src={slide.src} alt="" draggable={false} />
      )}
      {slide.type === 'grab' && <GrabSlide active={index === slides.findIndex(s => s.type === 'grab')} />}
      {slide.type === 'loop' && <LoopSlide active={index === slides.findIndex(s => s.type === 'loop')} />}
      <div className="deck-counter">{index + 1} / {slides.length}</div>
    </div>
  );
};

export default SlideDeck;
